import $script from 'scriptjs';
import axios from 'axios';
import Cookies from 'js-cookie';
import { triggerCustomEvent } from 'lib/helpers';
import {
  fromSignup,
  removeSocialOauthCookie,
  showSignupSurvey,
  socialSessionLoading,
  socialSessionError,
  socialSessionFromFollow,
} from 'lib/social-session-helpers';
import {
  findReferringCategory,
  sendTrackingEvent,
  trackCompleteEvent,
} from 'components/analytics/conversions/tracking';
import { JOIN_COMPLETE, LOGIN_COMPLETE, JOIN_LOGIN_TRIGGERED } from 'components/analytics/conversions/constants/actions';

type GoogleIdContext = 'signin' | 'signup' | 'use';

export default class GoogleLogin {
  // #region Types
  declare buttonTarget:HTMLElement;

  declare textTarget:HTMLElement;

  declare csrfToken:string;

  declare omniauthToken:string;

  declare originalText:string;
  // #endregion

  constructor({
    buttonTarget, textTarget = null, csrfToken, omniauthToken,
  }: { buttonTarget:HTMLElement, textTarget?:HTMLElement, csrfToken:string, omniauthToken:string }) {
    this.buttonTarget = buttonTarget;
    this.textTarget = textTarget;
    this.csrfToken = csrfToken;
    this.omniauthToken = omniauthToken;
    this.handleGoogleResponse = this.handleGoogleResponse.bind(this);
    this.handleError = this.handleError.bind(this);
    this.init();
  }

  init() {
    // Verifies that we're not using the deprecated Google api
    Cookies.set('G_AUTH2_MIGRATION', 'enforce');

    socialSessionError(false, this.buttonTarget);

    const libUrl = this.buttonTarget.dataset.googleSigninUrl;

    $script(libUrl, this.loadGoogleApi);
  }

  loadGoogleApi = () => {
    this.buttonTarget.dataset.loaded = 'true';
  }

  connect() {
    if (this.textTarget) {
      this.originalText = this.textTarget.innerHTML;

      socialSessionLoading(this.buttonTarget);
    }

    socialSessionError(false, this.buttonTarget);

    this.login();
  }

  login() {
    // Remove g_state cookie so prompt will be shown even if recently dismissed
    const cookieDomainArray = window.location.hostname.split('.');
    const cookieDomain = `${cookieDomainArray[1]}.${cookieDomainArray[2]}`;
    Cookies.remove('g_state', { domain: cookieDomain });

    const idConfig = {
      client_id: this.buttonTarget.dataset.appId,
      callback: this.handleGoogleResponse,
      context: this.buttonTarget.dataset.context as GoogleIdContext,
      state_cookie_domain: this.buttonTarget.dataset.stateCookieDomain,
      auto_select: this.buttonTarget.dataset.autoSelect === 'true',
    };

    google.accounts.id.initialize(idConfig);

    google.accounts.id.prompt(this.sendDialogTriggerEvent);
  }

  sendDialogTriggerEvent = (notification) => {
    if (!notification.isDisplayed()) return;

    sendTrackingEvent({
      element: this.buttonTarget,
      data: { action: JOIN_LOGIN_TRIGGERED },
    });
  }

  handleGoogleResponse(response) {
    if (response && !response.error) {
      // google authentication succeed, now post data to server.
      const newsletter = document.getElementById('user_newsletter')as HTMLInputElement;
      let newsletterValue = false;
      if (newsletter !== null && newsletter.type !== 'hidden') {
        newsletterValue = newsletter.checked;
      } else if (newsletter !== null && newsletter.type === 'hidden') {
        newsletterValue = true;
      }

      Cookies.set('g_csrf_token', this.csrfToken);

      axios.defaults.headers.common['X-CSRF-Token'] = this.csrfToken;

      axios.post(`/google_one_tap?sign_in_type=oauth&g_csrf_token=${this.csrfToken}&state=${this.omniauthToken}&newsletter=${newsletterValue}`, response)
        .then(this.responseSuccess)
        .catch(this.handleError);
    } else { this.handleError(response?.error); }
  }

  responseSuccess = (response) => {
    if (response.status === 200 || response.status === 204) {
      this.processResponse(response);
    } else { this.handleError(response.error); }

    removeSocialOauthCookie();
  }

  processResponse(response) {
    // using the social_oauth_type cookie to check if it's javascript
    if (fromSignup()) {
      // we have to eval the modal injector code coming back
      if (showSignupSurvey(response)) eval(response.data); // eslint-disable-line no-eval

      trackCompleteEvent({
        action: JOIN_COMPLETE,
        category: findReferringCategory(this.buttonTarget),
        label: 'Sign_Up-Google',
        dataLayerCompleteAttrs: [
          'email_address',
          'email_hash_md5',
          'email_hash_sha1',
          'sign_in_type',
        ],
      });

      return; // the rest of the function is for logins
    }

    trackCompleteEvent({
      action: LOGIN_COMPLETE,
      category: findReferringCategory(this.buttonTarget),
      label: 'Sign_In-Google',
      dataLayerCompleteAttrs: [
        'email_address',
        'email_hash_md5',
        'email_hash_sha1',
        'sign_in_type',
        'name',
        'country',
        'postal_code',
        'team_owner_email',
        'team_seats',
        'multiple_logins',
      ],
    });

    if (this.fromFollow) {
      window.location.reload();
    } else if (response.data.message === 'login_in_place') {
      triggerCustomEvent('LoggedInPlace');
    } else if (Cookies.get('sign_in_referrer')) {
      window.location.assign(Cookies.get('sign_in_referrer'));
    } else {
      window.location.reload();
    }
  }

  get fromFollow() {
    return socialSessionFromFollow();
  }

  handleError = (error) => {
    if (error) console.error(error); // eslint-disable-line no-console
    if (this.textTarget) socialSessionLoading(this.buttonTarget, this.originalText);
    socialSessionError(true, this.buttonTarget);
  }
}
